/**
 * 「删除账户与全部数据」确认对话框 —— **N4 §5.4.3 的确认强度**（勾选已导出 + 二次输入账号名）
 *
 * 依据：N 域 PRD **§5.4.3**（确认强度 · 冷静期 · 前置校验）· **§7.4 `AC-N4.24`** ·
 * 原型 `08-家庭与设置_原型.html` 的「态展示 · 注销账号」四步。
 *
 * ## 🔴 为什么内容层要**单独导出**（`DeletionDialogBody`）
 *
 * MUI `Dialog` 走 **Portal** ⇒ 子树被挂到 `document.body`，而端侧测试基座是
 * `renderToStaticMarkup`（**无 DOM**）⇒ 直接断言"对话框内容在不在产物里"会得到**假红/假绿两种
 * 不可靠结果**（㉝ 批与 `60` 批同一课）。⇒ 内容层**受控化 + 单独导出**（可静态断言），
 * 外壳只管 `open` / `onClose`。并配一条**结构断言**钉住"外壳在静态渲染下不产出内容"
 * —— 哪天 MUI 改了 Portal 行为，那条会红 ⇒ 提醒重评测试策略（而不是静默变更）。
 *
 * ## 🔴 三条 UI 纪律
 *
 * | # | 纪律 | 怎么做的 |
 * |---|---|---|
 * | ① | **确认强度与「解散账本」同级** | 勾选「已导出」**且**二次输入**账号名**（两者缺一，确认按钮恒禁用） |
 * | ② | **危险色只用于"不可逆"这件事本身** | ⚠️ 铁律 8：红=正（收入/涨）；这里用的是 MUI 的 `error` 语义色，**不是** `--c-alert` 的"已超"语义 —— 注销确实是"不可逆的破坏性动作"，属**唯一**该用它的场景 |
 * | ③ | **冷静期天数从共享层常量算** | `DELETION_COOLING_DAYS`（禁在端侧写死 `7`） |
 *
 * ## ⚠️ owner 仍有成员（`AC-N4.29`）的提示走**服务端 message**
 *
 * 服务端 409 的 `message` 已经点明"账本「X」还有其他成员（N 位）。请先转让或解散" ⇒
 * 端侧**原样展示**（本文件只补一句**静态**引导语，不重写服务端那份带账本名的文案）。
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { requestDeletion } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  DELETION_CANCEL,
  DELETION_CONFIRM,
  DELETION_COOLING_NOTE,
  DELETION_EXPORT_LABEL,
  DELETION_OWNER_BLOCKED_NOTE,
  DELETION_PURGE_NOTE,
  DELETION_STEP_CONFIRM,
  DELETION_STEP_COOLING,
  DELETION_STEP_EXPORT,
  DELETION_STEP_PURGE,
  DELETION_WARN,
  DELETE_TITLE,
  deletionConfirmHint,
} from '../features/sovereignty-labels.js';

/** 内容层 props —— 🔴 **全受控**（父级持状态），这样它才能被静态渲染精确断言 */
export interface DeletionDialogBodyProps {
  /** 当前账号名（二次输入的判据 —— 由会话提供） */
  accountName: string;
  exported: boolean;
  onExportedChange: (next: boolean) => void;
  confirmName: string;
  onConfirmNameChange: (next: string) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/** 四步说明（原型逐字浓缩） */
const STEPS: readonly { title: string; note: string }[] = [
  { title: DELETION_STEP_EXPORT, note: DELETION_EXPORT_LABEL },
  { title: DELETION_STEP_CONFIRM, note: '' },
  { title: DELETION_STEP_COOLING, note: DELETION_COOLING_NOTE },
  { title: DELETION_STEP_PURGE, note: DELETION_PURGE_NOTE },
];

export function DeletionDialogBody(props: DeletionDialogBodyProps) {
  // 🔴 两个条件**缺一不可**（`AC-N4.24`）：勾选已导出 + 二次输入**逐字等于**账号名
  const canConfirm = props.exported && props.confirmName === props.accountName && !props.busy;

  return (
    <Box sx={{ p: 3 }} data-testid="deletion-dialog-body">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
        {DELETE_TITLE}
      </Typography>
      <Alert severity="warning" sx={{ mt: 1 }} data-testid="deletion-warn">
        {DELETION_WARN}
      </Alert>

      <Box component="ol" sx={{ mt: 1.5, mb: 1, pl: 2.5 }}>
        {STEPS.map((s) => (
          <li key={s.title}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
              {s.title}
            </Typography>
            {s.note === '' ? null : (
              <Typography variant="caption" color="text.secondary" component="div">
                {s.note}
              </Typography>
            )}
          </li>
        ))}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={props.exported}
            onChange={(e) => {
              props.onExportedChange(e.target.checked);
            }}
            data-testid="deletion-exported-check"
          />
        }
        label={<Typography variant="caption">{DELETION_EXPORT_LABEL}</Typography>}
      />

      <TextField
        fullWidth
        size="small"
        sx={{ mt: 1.5 }}
        label={DELETION_STEP_CONFIRM}
        value={props.confirmName}
        onChange={(e) => {
          props.onConfirmNameChange(e.target.value);
        }}
        helperText={deletionConfirmHint(props.accountName)}
        inputProps={{ 'data-testid': 'deletion-confirm-input', maxLength: 50 }}
      />

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {DELETION_OWNER_BLOCKED_NOTE}
      </Typography>

      {props.error !== null ? (
        <Alert severity="error" sx={{ mt: 1.5 }} data-testid="deletion-error">
          {props.error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
        <Button size="small" variant="outlined" onClick={props.onCancel} disabled={props.busy}>
          {DELETION_CANCEL}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={!canConfirm}
          data-testid="deletion-confirm"
          onClick={props.onConfirm}
        >
          {props.busy ? '处理中…' : DELETION_CONFIRM}
        </Button>
      </Stack>
    </Box>
  );
}

/** 外壳 —— 只负责 `open` / 状态与提交（内容全在 {@link DeletionDialogBody}） */
export function DeletionDialog({
  open,
  onClose,
  accountName,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  accountName: string;
  onSubmitted: () => void;
}) {
  const qc = useQueryClient();
  const [exported, setExported] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await requestDeletion(confirmName);
      void qc.invalidateQueries({ queryKey: ['deletion'] });
      onSubmitted();
    } catch (e: unknown) {
      // 🔴 原样展示服务端 `message`（owner 仍有成员那条带**账本名与成员数**，端侧编不出来）
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DeletionDialogBody
        accountName={accountName}
        exported={exported}
        onExportedChange={setExported}
        confirmName={confirmName}
        onConfirmNameChange={setConfirmName}
        busy={busy}
        error={error}
        onCancel={onClose}
        onConfirm={() => {
          void submit();
        }}
      />
    </Dialog>
  );
}
